import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import compact from 'lodash-es/compact';
import difference from 'lodash-es/difference';
import find from 'lodash-es/find';
import take from 'lodash-es/take';
import union from 'lodash-es/union';
import without from 'lodash-es/without';
import { Subject } from 'rxjs';

import { SAMPLES_CONFIG } from '@df/samples/config/samples.config';
import { Sample } from '@df/samples/sample';
import { SampleFactory } from '@df/samples/sample.factory';
import { SamplesBasket } from '@df/samples/samples-basket';
import { NSamples } from '@df/samples/samples.namespace';

import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Status } from '@df/core/status';
import { Customer } from '@df/session/api/customer';
import { CustomerModel } from '@df/session/api/customer.model';

@Injectable({ providedIn: 'root' })
export class Samples {
    updateSubject = new Subject<void>();

    addSubject = new Subject<Product<IProductDataV2>>();

    status = new Status();

    private _items: Sample[] = [];
    private _data: number[] = [];

    constructor(
        private sampleFactory: SampleFactory,
        private samplesBasket: SamplesBasket,
        private customer: Customer,
        private customerModel: CustomerModel,
        private localStorageService: LocalStorageService,
        private requestService: RequestService,
        private router: Router,
        @Inject(SAMPLES_CONFIG) private configuration: NSamples.IConfig
    ) {
        this.register();
        this.onInit();
        this.samplesBasket.basketUpdateSubject.subscribe(() => this.onSamplesBasketUpdate());
        this.customerModel.obs.status.subscribe(() => {
            if (!this.customer.model.isLoggedIn) {
                this.reset();
            }
        });
    }

    get config(): NSamples.IConfig {
        return this.configuration;
    }

    get limit(): number | undefined {
        return this.config.limit;
    }

    private get storageName(): string {
        return 'customerSamples';
    }

    private onSamplesBasketUpdate(): void {
        if (this.samplesBasket && !this.samplesBasket.items?.length) {
            this.reset();
        }
    }

    protected onInit(): void {
        this.setData(this.localStorageService.get(this.storageName) || []);
    }

    reset(): void {
        this.setData([]);
    }

    /**
     * Data setter
     * Syncs data with local storage and items
     * @param  {number[]} data
     */
    setData(data: number[]) {
        let dataToSet = data.filter(i => i > 0);
        // app
        if (this.limit !== undefined) {
            dataToSet = take(dataToSet, this.limit);
        }
        this.localStorageService.set(this.storageName, dataToSet);
        this._data = dataToSet;

        // remove no longer existing
        const toRemove = this._items.filter(item => this._data.indexOf(item.productId) === -1);
        toRemove.forEach(item => item.onDestroy());
        this._items = difference(this._items, toRemove);

        // add new ones
        difference(
            this._data,
            this._items.map(i => i.productId)
        ).forEach(productId => this._items.push(this.sampleFactory.create(productId, this)));

        this.next();
    }

    protected next() {
        this.updateSubject.next();
    }

    /**
     * Data getter
     * @return {number[]} [description]
     */
    get data(): number[] {
        return this._data;
    }

    /**
     * Items getter
     * @return {Sample[]}
     */
    get items(): Sample[] {
        return this._items;
    }

    /**
     * Registers that we want sample for given product
     * @param  {Product} product
     */
    addSampleFor(product: Product<IProductDataV2>): void {
        if (!this.canAddSampleFor(product)) {
            return;
        }

        this.setData(union(this.data, [product.id]));
        this.addSubject.next(product);

        if (this.config.toWishlist) {
            this.customer.wishlist.add(undefined, { product: product.id });
        }
    }

    /**
     * Toggles sample for product
     * @param  {Product} product
     */
    toggleSampleFor(product: Product<IProductDataV2>): void {
        if (this.getSampleFor(product)) {
            this.removeSampleFor(product);
        } else {
            this.addSampleFor(product);
        }
    }

    /**
     * Removes samples for product
     * @param  {Product} product
     */
    removeSampleFor(product: Product<IProductDataV2> | number): void {
        // WARNING: you have to set data when you change items!
        this.setData(without(this.data, typeof product == 'number' ? product : product.id));
    }

    /**
     * Gets sample item data (added to samples)
     * @param  {Product} product Product we want sample data for
     * @return {ISampleDataItem}          Sample product data
     */
    getSampleFor(product: Product<IProductDataV2> | number): Sample | undefined {
        const id = typeof product == 'number' ? product : product.id;
        return find(this._items, item => item['productId'] === id);
    }

    /**
     * Checks if sample is added for given product
     * @param  {Product | number}      product
     * @return {boolean}
     */
    hasSampleFor(product: Product<IProductDataV2> | number): boolean {
        return !!this.getSampleFor(product);
    }
    /**
     * Checks if sample is added for given product
     * @param  {Product | number}      product
     * @return {boolean}
     */
    canAddSampleFor(product: Product<IProductDataV2>): boolean {
        return !this.hasSampleFor(product) && !!this.getSampleId(product) && (this.limit === undefined || this.limit > this.data.length);
    }
    /**
     * Retrives sample product id for given product
     * @param  {Product} product Product we want sample for
     * @return {number}          Sample product id
     */
    getSampleId(product: Product<IProductDataV2>): number | undefined {
        return product && product.data ? product.data.sample : undefined;
    }

    /**
     * Loads all sample products in pack
     */
    load(status?: Status): Promise<void> {
        const promises = compact(this._items.map(item => item.load())).map(
            promise => new Promise<void>(resolve => promise.finally(resolve))
        );
        return this.requestService.send(
            status,
            () => {
                return Promise.all(promises);
            },
            res => {
                this.cleanupData();
                return res;
            },
            res => {
                return res;
            }
        );
    }
    /**
     * cleanup data - we may have invalid items in storage
     */
    protected cleanupData() {
        this.setData(
            this.data.filter(productId => {
                const item = this.getSampleFor(productId);
                return item && item.hasValidProducts();
            })
        );
    }

    /**
     * Sends submission request
     * Adds samples to sample quote
     * Then redirects to samples checkout
     */
    submit(): Promise<any> {
        return this.createQuote(this.status, () => {
            if (this.config.checkoutUrl) {
                this.router.navigate([this.config.checkoutUrl]);
            }
        });
    }

    /**
     * Sends request to BE
     */
    createQuote(status?: Status, onSuccess?: IRequestCallback, onError?: IRequestCallback): Promise<any> {
        return this.requestService.send(
            status,
            {
                path: NSamples.ENDPOINTS.createQuote,
                data: this.getSubmissionData()
            },
            onSuccess,
            onError
        );
    }

    loadCheckout(status?: Status): Promise<Checkout.IData> {
        return new Promise((resolve, reject) => {
            this.requestService
                .send<Checkout.ILoadResponse>(status, { path: NSamples.ENDPOINTS.checkoutData })
                .then(result => {
                    const checkoutData = result?.checkout;
                    if (checkoutData) {
                        resolve(checkoutData);
                    } else {
                        reject();
                    }
                })
                .catch(reject);
        });
    }

    loadCheckoutSuccess(status?: Status): Promise<Checkout.ISuccessResponse> {
        return this.requestService.send<Checkout.ISuccessResponse>(status, { path: NSamples.ENDPOINTS.checkoutSuccessData });
    }

    /**
     * Prepares data for submission
     * @return {IAddToBasketRequestData}
     */
    private getSubmissionData(): NSamples.ISamplesAddToBasketRequestData {
        return {
            products: compact(this._items.map(item => item.toBasketRequest())),
            sampleQuote: true
        };
    }

    private register() {
        this.customer.register(
            {
                id: 'samples',
                listener: 'customersamples',
                account: false
            },
            this
        );
    }
}
